// コメント添付画像のアップロード処理。
//
// 画像オブジェクト（uploadAndPlaceImage）と同じ添付基盤を使うが、コメントは「貼り付けただけで
// 送信をやめる」操作が起こりやすいため、昇格（attachments/ への promote）は送信確定まで遅らせる
// 二段階方式にする:
// 1) upload（貼り付け時）: prepareImageForUpload で検証・自動縮小 →
//    /uploads/images:presign で pending キーへの署名付き PUT URL を取得 → S3 へ直接 PUT。
//    実体は pending/<canvasId>/... に置かれたままで、送信せずに取り消した場合は S3 Lifecycle
//    （pending-cleanup: 1 日）が自動回収する（クライアントからの明示削除は不要）。
// 2) finalize（送信確定時）: /uploads:complete で attachments/<canvasId>/... へ昇格し、
//    公開 URL を得てメッセージの images へ保存する。
//
// コメントはオブジェクト作成より先に画像を確定させるため、presign の objectId には
// コメント専用の一意 ID を都度採番する。presign はオブジェクトの存在を要求せず、
// objectId は S3 キーの名前空間としてのみ使われる。

import { customAlphabet } from "nanoid";
import { COMMENT_IMAGE_URL_MAX_LENGTH } from "@refinement-canvas/domain";
import type { CanvasApiClient } from "../../../lib/canvas-api";
import { prepareImageForUpload } from "../image-resize";
import { describeUploadError, putToPresignedUrl } from "../upload-limits";

/** PUT 済みで昇格待ちの pending 実体の参照（finalize への入力）。 */
export interface CommentImagePendingUpload {
  readonly bucket: string;
  readonly key: string;
}

/** 貼り付け時アップロード（pending への PUT まで）の結果。 */
export type CommentImageUploadResult =
  | { readonly ok: true; readonly pending: CommentImagePendingUpload }
  | { readonly ok: false; readonly message: string };

/** 送信確定時の昇格（公開 URL の確定）の結果。 */
export type CommentImageFinalizeResult =
  | { readonly ok: true; readonly url: string }
  | { readonly ok: false; readonly message: string };

/** コメント添付画像のアップローダ（貼り付け時 upload / 送信確定時 finalize の二段階）。 */
export interface CommentImageUploader {
  readonly upload: (file: File) => Promise<CommentImageUploadResult>;
  readonly finalize: (pending: CommentImagePendingUpload) => Promise<CommentImageFinalizeResult>;
}

// presign の objectId 制約（[A-Za-z0-9_-]+）を満たす一意 ID（dispatcher の generateId と同じ字種）。
const generateCommentUploadId = customAlphabet(
  "23456789abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ",
  16
);

/** 指定キャンバス向けのコメント画像アップローダを生成する。 */
export function createCommentImageUploader(
  canvasApi: CanvasApiClient,
  canvasId: string
): CommentImageUploader {
  return {
    async upload(file: File): Promise<CommentImageUploadResult> {
      // 画像オブジェクトと同じ前処理（0 バイト・非対応形式・上限超過の検証と自動縮小）。
      const prepared = await prepareImageForUpload(file);
      if (!prepared.ok) return { ok: false, message: prepared.message };
      const uploadFile = prepared.file;
      try {
        const presigned = await canvasApi.presignImageUpload({
          canvasId,
          objectId: `comment-${generateCommentUploadId()}`,
          contentType: uploadFile.type || "image/png",
          contentLength: uploadFile.size,
        });
        await putToPresignedUrl(presigned.uploadUrl, uploadFile, uploadFile.type || "image/png");
        return { ok: true, pending: { bucket: presigned.bucket, key: presigned.key } };
      } catch (e) {
        console.warn("[upload] コメント画像のアップロードに失敗", e);
        return { ok: false, message: describeUploadError("image", e) };
      }
    },

    async finalize(pending: CommentImagePendingUpload): Promise<CommentImageFinalizeResult> {
      try {
        const completed = await canvasApi.notifyUploadComplete(
          canvasId,
          pending.bucket,
          pending.key
        );
        if (!completed.publicUrl) {
          // 公開 URL が無い環境（ATTACHMENTS_PUBLIC_BASE_URL 未設定の dev/mock）では、他参加者が
          // 表示できる URL をメッセージへ載せられないため、添付自体を失敗として扱う。
          return {
            ok: false,
            message:
              "この環境では添付画像の公開 URL を発行できないため、コメントに画像を添付できません。",
          };
        }
        if (completed.publicUrl.length > COMMENT_IMAGE_URL_MAX_LENGTH) {
          // サーバ側スキーマ（COMMENT_IMAGE_URL_MAX_LENGTH）で reject される URL を送らない防御。
          // 通常のキー構成では到達しない。
          return {
            ok: false,
            message: "添付画像の URL が長すぎるため、コメントに添付できません。",
          };
        }
        return { ok: true, url: completed.publicUrl };
      } catch (e) {
        console.warn("[upload] コメント画像の確定（昇格）に失敗", e);
        return { ok: false, message: describeUploadError("image", e) };
      }
    },
  };
}
